import React, { FunctionComponent } from 'react'
import { Markup } from 'interweave'
import useTranslation from '../../hooks/useTranslation'
import TechnicalData from '../../entities/TechnicalData'
import { Table, Td, Tr } from '../table'
import DisclaimerText, { getRemarkLabels } from '../disclaimer-text'
import style from './technicalDataTable.css'
import InfoButton from '../info-button'
import InfoI from '../info-i'
import normalizeString from '../../helpers/normalizeString'

export type Props = {
    technicalData: TechnicalData
    openInfoModal: Function
}

const TechnicalDataTable: FunctionComponent<Props> = (props) => {
    const { technicalData: data, openInfoModal } = props
    const { technicalData, remarks } = data
    const { t } = useTranslation()

    if (technicalData.length === 0) {
        return null
    }

    const remarkLabels = getRemarkLabels(remarks)

    const tableElements = technicalData.map((technicalDate) => {
        const dataEntryElements = technicalDate.entry.map((entry) => {
            const { media, name } = entry

            const openModal = (activeMedia: string|null = null): Function => () => openInfoModal({
                title: name,
                activeMedia,
                media,
            })

            const infoIsForced = media && media.find((mediaItem) => mediaItem.forced) !== undefined
            const hasInfo = entry.media !== null
            const infoButtonElement = hasInfo ? (
                <InfoButton openModal={openModal()} dataTestId={`info-button-${entry.id}`}>
                    <InfoI isForced={infoIsForced} />
                </InfoButton>
            ) : null

            const sanitizedEntryId = entry.id.replaceAll(":", "_")
                .replaceAll("|", "_")
                .replaceAll(".", "_")

            const entryName = t(`technicalData.entries.${sanitizedEntryId}`) === `technicalData.entries.${sanitizedEntryId}`
                ? normalizeString(entry.name)
                : t(`technicalData.entries.${sanitizedEntryId}`)

            return (
                <Tr key={entry.id}>
                    <Td className={style.nameCol}>
                        <div className={style.nameContainer}>
                            <div>
                                <Markup content={entryName} tagName="span" />
                                {' '}
                                {remarkLabels[entry.nameRemarkRefId]}
                                {' '}
                            </div>
                            <span className={style.infoButtonWrapper}>
                                {infoButtonElement}
                            </span>
                        </div>
                    </Td>
                    <Td className={style.valueCol} data-testid="tech-data-value">
                        <Markup content={normalizeString(entry.value)} tagName="span" />
                        {' '}
                        {remarkLabels[entry.valueRemarkRefId]}
                    </Td>
                </Tr>
            )
        })

        const linkElement = technicalDate.link !== null ? (
            <a href={technicalDate.link} target="_blank" rel="noopener noreferrer">
                {t(`technicalDataLinks.${technicalDate.id}`)}
            </a>
        ) : null

        const sectionName= t(`technicalData.sections.${technicalDate.id}`) === `technicalData.sections.${technicalDate.id}`
            ? technicalDate.headline
            : t(`technicalData.sections.${technicalDate.id}`)

        return (
            <section key={technicalDate.id} data-testid={`tech-data-${technicalDate.id}`}>
                <div className={style.headingWrapper}>
                    <Markup content={sectionName} tagName="h3" className={style.title} />
                    {linkElement}
                </div>
                <Table className={style.table}>
                    <tbody>
                        {dataEntryElements}
                    </tbody>
                </Table>
            </section>
        )
    })

    return (
        <>
            {tableElements}
            <DisclaimerText remarks={remarks} />
        </>
    )
}

export default TechnicalDataTable
